import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, Truck, AlertOctagon, LocateFixed, Activity, 
  CheckCircle2, TrendingUp, Clock, ArrowUpRight, ArrowDownRight,
  MoreVertical
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { adminAPI, systemAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { ROLES, PRIVILEGES } from '../config/roles';
import { formatIstDateTime } from '../utils/formatIst';
import { resolveUploadUrl } from '../utils/resolveUploadUrl';
import {
    formatPlaceLabelFromCustomer,
    resolvePlaceLabelForRep,
} from '../utils/reverseGeocode';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

/** Human-readable place label for map + list (customer/site name or geocoded area). */
const formatPlaceLabel = (rep) => formatPlaceLabelFromCustomer(rep);

const DEFAULT_GPS_UPDATE_MS = 5000;

const EMPTY_OVERVIEW_STATS = {
    active_executives: { online: 0, total: 0, on_duty: 0 },
    visits_today: 0,
    visits_yesterday: 0,
    distance_km_today: 0,
    distance_km_yesterday: 0,
    security_alerts_today: 0,
    security_alerts_yesterday: 0,
    recent_activity: [],
};

const formatDayTrend = (today, yesterday) => {
    if (yesterday === 0) {
        if (today === 0) return null;
        return { trend: 'up', trendValue: `+${today}` };
    }
    const pct = Math.round(((today - yesterday) / yesterday) * 100);
    return {
        trend: pct >= 0 ? 'up' : 'down',
        trendValue: `${pct >= 0 ? '+' : ''}${pct}%`,
    };
};

const formatCountTrend = (today, yesterday) => {
    const diff = today - yesterday;
    if (diff === 0) return null;
    return {
        trend: diff > 0 ? 'up' : 'down',
        trendValue: `${diff > 0 ? '+' : ''}${diff}`,
    };
};

const formatRelativeTime = (value) => {
    if (!value) return '—';
    const ts = new Date(String(value).replace(' ', 'T')).getTime();
    if (Number.isNaN(ts)) return formatIstDateTime(value);
    const mins = Math.max(0, Math.round((Date.now() - ts) / 60000));
    if (mins === 0) return 'Just now';
    if (mins === 1) return '1 min ago';
    if (mins < 60) return `${mins} mins ago`;
    const hours = Math.floor(mins / 60);
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    return formatIstDateTime(value);
};

const ONLINE_MS = 5 * 60 * 1000;

const isRepOnline = (rep) => {
    if (rep?.presence === 'on_duty' || rep?.presence === 'online') return true;
    if (!rep?.last_seen) return false;
    const ts = new Date(String(rep.last_seen).replace(' ', 'T')).getTime();
    return Number.isFinite(ts) && Date.now() - ts <= ONLINE_MS;
};

const PRESENCE_META = {
    on_duty: { label: 'On duty', color: '#059669', dot: 'bg-emerald-500', pulse: true },
    online: { label: 'Available (live GPS)', color: '#2563eb', dot: 'bg-blue-500', pulse: true },
    stale: { label: 'Last seen (stale)', color: '#d97706', dot: 'bg-amber-500', pulse: false },
    offline: { label: 'Offline / no GPS', color: '#94a3b8', dot: 'bg-slate-400', pulse: false },
};

const presenceKey = (rep) => {
    if (rep?.presence && PRESENCE_META[rep.presence]) return rep.presence;
    if (rep?.on_duty) return 'on_duty';
    return isRepOnline(rep) ? 'online' : 'offline';
};

function offsetLatLng(lat, lng, index, total) {
    if (total <= 1) return [lat, lng];
    const angle = (index / total) * 2 * Math.PI;
    const delta = 0.0014 * (1 + Math.floor(index / 6));
    return [lat + delta * Math.cos(angle), lng + delta * Math.sin(angle)];
}

function FitMapBounds({ positions, boundsKey }) {
    const map = useMap();
    useEffect(() => {
        if (!positions?.length) return;
        if (positions.length === 1) {
            map.setView(positions[0], 13);
            return;
        }
        map.fitBounds(L.latLngBounds(positions), { padding: [48, 48], maxZoom: 14 });
    }, [map, boundsKey]);
    return null;
}

const parseGpsUpdateInterval = (settingsData) => {
    let data = settingsData;
    if (Array.isArray(data)) data = data[0];
    const ms = parseInt(data?.gps_update_interval, 10);
    return Number.isFinite(ms) && ms >= 1000 ? ms : DEFAULT_GPS_UPDATE_MS;
};

const coordsKey = (lat, lng) => {
    if (lat == null || lng == null) return null;
    return `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;
};

const mergeRepLocations = (repsArray, locArray, previousReps = []) => {
    const locById = Object.fromEntries(locArray.map((l) => [l.id, l]));
    const prevById = Object.fromEntries(previousReps.map((r) => [r.id, r]));

    return repsArray.map((r) => {
        const loc = locById[r.id];
        const lat = loc?.lat;
        const lng = loc?.lng;
        const mergedRep = {
            ...r,
            lat,
            lng,
            last_seen: loc?.last_seen,
            customer_name: loc?.customer_name,
            customer_location: loc?.customer_location,
            on_duty: Boolean(loc?.on_duty),
            presence: loc?.presence || null,
            has_gps: loc?.has_gps ?? (lat != null && lng != null),
            location_source: loc?.source || null,
            status: loc?.status || (r.is_active ? 'Active' : 'Idle'),
        };

        const customerLabel = formatPlaceLabel(mergedRep);
        if (customerLabel) {
            return { ...mergedRep, placeLabel: customerLabel };
        }

        const prev = prevById[r.id];
        const prevKey = coordsKey(prev?.lat, prev?.lng);
        const nextKey = coordsKey(lat, lng);
        if (prev?.placeLabel && prevKey && nextKey && prevKey === nextKey) {
            return { ...mergedRep, placeLabel: prev.placeLabel };
        }

        return { ...mergedRep, placeLabel: null };
    });
};

const createRepMarkerIcon = (rep) => {
    const repName = rep.name || rep.email || 'Rep';
    const place = rep.placeLabel || (rep.on_duty ? 'On duty' : 'Locating…');
    const key = presenceKey(rep);
    const meta = PRESENCE_META[key] || PRESENCE_META.offline;
    const pulseRing = meta.pulse
        ? `<div style="
            position:absolute;width:28px;height:28px;border-radius:50%;
            background:${meta.color}33;animation:repPulse 2s ease-in-out infinite;
            top:50%;left:50%;transform:translate(-50%,-50%);
          "></div>`
        : '';

    return L.divIcon({
        className: '',
        html: `
          <style>@keyframes repPulse{0%,100%{transform:translate(-50%,-50%) scale(0.9);opacity:.9}50%{transform:translate(-50%,-50%) scale(1.15);opacity:.35}}</style>
          <div style="display:flex;flex-direction:column;align-items:center;transform:translate(-50%,-100%);">
            <div style="
              background:#fff;border:2px solid ${meta.color};border-radius:8px;
              padding:4px 8px;box-shadow:0 2px 8px rgba(15,23,42,0.15);
              max-width:200px;text-align:center;margin-bottom:4px;
            ">
              <div style="font-size:11px;font-weight:700;color:#0f172a;line-height:1.2;">${escapeHtml(repName)}</div>
              <div style="font-size:9px;font-weight:700;color:${meta.color};line-height:1.2;margin-top:2px;text-transform:uppercase;">${escapeHtml(meta.label)}</div>
              <div style="font-size:10px;font-weight:600;color:#64748b;line-height:1.2;margin-top:2px;">${escapeHtml(place)}</div>
            </div>
            <div style="position:relative;width:18px;height:18px;">
              ${pulseRing}
              <div style="
                position:relative;z-index:2;width:14px;height:14px;background:${meta.color};border:2px solid #fff;
                border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.25);
              "></div>
            </div>
          </div>
        `,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
    });
};

const StatCard = ({ title, value, icon: Icon, color, trend, trendValue, delay }) => (
    <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay }}
        className="glass-card p-6 flex flex-col justify-between group hover:border-blue-500/30 transition-colors"
    >
        <div className="flex justify-between items-start">
            <div className={`p-3 rounded-2xl ${color} bg-opacity-10 transition-transform group-hover:scale-110 duration-300`}>
                <Icon className={`w-6 h-6 ${color.replace('bg-', 'text-')}`} />
            </div>
            {trend && trendValue != null && (
                <div className={`flex items-center gap-1 text-xs font-bold ${trend === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
                    {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {trendValue}
                </div>
            )}
        </div>
        <div className="mt-4">
            <p className="text-sm font-semibold text-slate-500 mb-1">{title}</p>
            <h3 className="text-3xl font-extrabold text-slate-900 tracking-tight">{value}</h3>
        </div>
    </motion.div>
);

const ActivityItem = ({ title, time, type, status }) => {
    const icons = {
        visit: <LocateFixed className="w-4 h-4 text-blue-500" />,
        alert: <AlertOctagon className="w-4 h-4 text-rose-500" />,
        checkin: <CheckCircle2 className="w-4 h-4 text-emerald-500" />
    };
    
    return (
        <div className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-xl transition-colors group cursor-pointer">
            <div className="w-10 h-10 rounded-xl bg-white border border-slate-100 shadow-sm flex items-center justify-center group-hover:border-blue-200 transition-colors">
                {icons[type] || icons.visit}
            </div>
            <div className="flex-1">
                <p className="text-sm font-bold text-slate-800">{title}</p>
                <div className="flex items-center gap-2 text-[10px] font-semibold text-slate-400">
                    <Clock className="w-3 h-3" /> {time}
                </div>
            </div>
            {status && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 uppercase tracking-wider">
                    {status}
                </span>
            )}
        </div>
    );
};

const RepItem = ({ rep }) => {
    const key = presenceKey(rep);
    const meta = PRESENCE_META[key] || PRESENCE_META.offline;

    return (
        <div className="flex items-center gap-4 p-4 border border-slate-100 rounded-2xl hover:border-blue-200 hover:shadow-lg hover:shadow-blue-500/5 transition-all group">
            <div className="relative">
                {rep.photo ? (
                    <img src={resolveUploadUrl(rep.photo) || rep.photo} alt="" className="w-12 h-12 rounded-xl object-cover" />
                ) : (
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-blue-100 to-indigo-50 border border-blue-200 flex items-center justify-center text-blue-700 font-bold">
                        {(rep.name || rep.email || 'R').charAt(0).toUpperCase()}
                    </div>
                )}
                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${meta.dot}`} title={meta.label} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800 truncate">{rep.name || rep.email}</p>
                <p className="text-xs font-semibold text-slate-400 truncate">{rep.email}</p>
                {rep.placeLabel ? (
                    <p className="text-[10px] font-medium text-blue-600 mt-1 truncate" title={rep.placeLabel}>
                        📍 {rep.placeLabel}
                    </p>
                ) : rep.lat != null && rep.lng != null ? (
                    <p className="text-[10px] font-medium text-slate-400 mt-1">📍 Locating…</p>
                ) : (
                    <p className="text-[10px] font-medium text-slate-400 mt-1">📍 No location yet</p>
                )}
            </div>
            <div className="text-right">
                <p className="text-xs font-bold" style={{ color: meta.color }}>
                    {meta.label}
                </p>
                <p className="text-[10px] font-bold text-slate-400 uppercase">
                    {rep.has_gps === false ? 'No GPS' : rep.location_source || 'GPS'}
                </p>
            </div>
        </div>
    );
};

const OverviewTab = () => {
    const navigate = useNavigate();
    const { hasPrivilege } = useAuth();
    const canAddExecutive = hasPrivilege(PRIVILEGES.CREATE_USER);
    const canViewReports =
        hasPrivilege(PRIVILEGES.VIEW_VISIT_HISTORY) || hasPrivilege(PRIVILEGES.VIEW_ANALYTICS);
    const reportsPath = hasPrivilege(PRIVILEGES.VIEW_ANALYTICS)
        ? '/analytics'
        : '/logs';
    const [reps, setReps] = useState([]);
    const [overviewStats, setOverviewStats] = useState(EMPTY_OVERVIEW_STATS);
    const [loading, setLoading] = useState(true);
    const [mapRefreshMs, setMapRefreshMs] = useState(DEFAULT_GPS_UPDATE_MS);
    const repsRef = useRef([]);

    useEffect(() => {
        const cancelled = { current: false };
        const intervalRef = { current: null };

        const geocodeMissing = (merged) => {
            merged.forEach((rep) => {
                if (rep.placeLabel || rep.lat == null || rep.lng == null) return;
                resolvePlaceLabelForRep(rep).then((label) => {
                    if (cancelled.current || !label) return;
                    setReps((prev) => {
                        const next = prev.map((r) =>
                            r.id === rep.id ? { ...r, placeLabel: label } : r
                        );
                        repsRef.current = next;
                        return next;
                    });
                });
            });
        };

        const fetchDashboardData = async () => {
            try {
                const [repData, locData, statsRes] = await Promise.all([
                    adminAPI.getAllReps(),
                    adminAPI.getRepLocations(),
                    adminAPI.getOverviewStats(),
                ]);
                if (cancelled.current) return;

                const statsPayload = statsRes.data?.data || EMPTY_OVERVIEW_STATS;
                setOverviewStats(statsPayload);

                const repsArray = Array.isArray(repData.data?.data) ? repData.data.data : [];
                const locArray = Array.isArray(locData.data?.data) ? locData.data.data : [];
                const merged = mergeRepLocations(repsArray, locArray, repsRef.current);
                repsRef.current = merged;
                setReps(merged);
                geocodeMissing(merged);
            } catch (error) {
                console.error('Failed fetching data', error);
            } finally {
                if (!cancelled.current) setLoading(false);
            }
        };

        const start = async () => {
            let refreshMs = DEFAULT_GPS_UPDATE_MS;
            try {
                const settingsRes = await systemAPI.getSettings();
                if (settingsRes.data?.success) {
                    refreshMs = parseGpsUpdateInterval(settingsRes.data.data);
                }
            } catch {
                /* use default interval */
            }
            if (cancelled.current) return;

            setMapRefreshMs(refreshMs);
            await fetchDashboardData();
            if (cancelled.current) return;

            intervalRef.current = setInterval(fetchDashboardData, refreshMs);
        };

        start();

        return () => {
            cancelled.current = true;
            clearInterval(intervalRef.current);
        };
    }, []);

    const repsWithGps = reps.filter(
        (r) => r.lat != null && r.lng != null && !Number.isNaN(r.lat) && !Number.isNaN(r.lng)
    );
    const repsAwaitingGps = reps.filter((r) => !repsWithGps.some((g) => g.id === r.id));

    const mapMarkers = (() => {
        const groups = {};
        repsWithGps.forEach((r) => {
            const k = coordsKey(r.lat, r.lng);
            if (!groups[k]) groups[k] = [];
            groups[k].push(r);
        });
        const out = [];
        Object.values(groups).forEach((group) => {
            group.forEach((rep, i) => {
                const [mapLat, mapLng] = offsetLatLng(rep.lat, rep.lng, i, group.length);
                out.push({ ...rep, mapLat, mapLng });
            });
        });
        return out;
    })();

    const mapPositions = mapMarkers.map((r) => [r.mapLat, r.mapLng]);
    const mapBoundsKey = mapMarkers.map((r) => `${r.id}:${r.mapLat},${r.mapLng}`).join('|');
    const onDutyCount = reps.filter((r) => presenceKey(r) === 'on_duty').length;

    if (loading) return (
        <div className="flex flex-col justify-center items-center h-96 gap-4">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-500 font-bold animate-pulse text-sm">Building Command Center...</p>
        </div>
    );

    const activeExecs = overviewStats.active_executives || EMPTY_OVERVIEW_STATS.active_executives;
    const visitsTrend = formatDayTrend(overviewStats.visits_today, overviewStats.visits_yesterday);
    const distanceTrend = formatDayTrend(
        overviewStats.distance_km_today,
        overviewStats.distance_km_yesterday
    );
    const alertsTrend = formatCountTrend(
        overviewStats.security_alerts_today,
        overviewStats.security_alerts_yesterday
    );
    const recentActivity = overviewStats.recent_activity || [];
    const mapCenter =
        mapMarkers.length > 0 ? [mapMarkers[0].mapLat, mapMarkers[0].mapLng] : [12.9716, 77.5946];

    return (
        <div className="admin-page space-y-8 pb-12">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Executive Command Center</h1>
                    <p className="text-slate-500 font-medium">Real-time overview of your field operations.</p>
                </div>
                <div className="admin-page-header-actions">
                    <button
                        type="button"
                        onClick={() => navigate(canViewReports ? reportsPath : '/overview')}
                        className="btn-premium bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                        title={
                            canViewReports
                                ? 'Open performance reports and visit analytics'
                                : 'Reports require visit-log access'
                        }
                    >
                        <TrendingUp className="w-4 h-4" /> Reports
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (canAddExecutive) {
                                navigate('/users', {
                                    state: { openCreate: true, defaultRole: ROLES.REP },
                                });
                            } else {
                                navigate('/users');
                            }
                        }}
                        className="btn-premium bg-blue-600 text-white shadow-lg shadow-blue-500/20 hover:bg-blue-700"
                        title={canAddExecutive ? 'Add a new field executive (rep)' : 'View users (no create permission)'}
                    >
                        Add Executive
                    </button>
                </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Active Executives"
                    value={`${activeExecs.online}/${activeExecs.total}`}
                    icon={Users}
                    color="bg-blue-500"
                    trend={activeExecs.on_duty > 0 ? 'up' : undefined}
                    trendValue={activeExecs.on_duty > 0 ? `${activeExecs.on_duty} on duty` : null}
                    delay={0.1}
                />
                <StatCard
                    title="Visits Today"
                    value={String(overviewStats.visits_today ?? 0)}
                    icon={CheckCircle2}
                    color="bg-emerald-500"
                    trend={visitsTrend?.trend}
                    trendValue={visitsTrend?.trendValue}
                    delay={0.2}
                />
                <StatCard
                    title="Distance Traveled"
                    value={`${overviewStats.distance_km_today ?? 0} km`}
                    icon={Truck}
                    color="bg-indigo-500"
                    trend={distanceTrend?.trend}
                    trendValue={distanceTrend?.trendValue}
                    delay={0.3}
                />
                <StatCard
                    title="Security Alerts"
                    value={String(overviewStats.security_alerts_today ?? 0)}
                    icon={AlertOctagon}
                    color="bg-rose-500"
                    trend={alertsTrend?.trend}
                    trendValue={alertsTrend?.trendValue}
                    delay={0.4}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 min-w-0">
                {/* Real-time Map Area */}
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    className="lg:col-span-8 glass-card overflow-hidden flex flex-col h-[500px] min-w-0"
                >
                    <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 min-w-0">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                                <LocateFixed className="w-4 h-4 text-blue-600" />
                            </div>
                            <h2 className="text-lg font-bold text-slate-800">Global Executive Map</h2>
                        </div>
                        <div className="flex flex-col items-start sm:items-end gap-2 min-w-0 w-full sm:w-auto">
                           <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest text-left sm:text-right break-words">
                               {mapMarkers.length}/{reps.length} on map · {onDutyCount} on duty · every {(mapRefreshMs / 1000).toFixed(mapRefreshMs % 1000 === 0 ? 0 : 1)}s
                           </span>
                           <div className="flex flex-wrap gap-2 justify-end">
                             {Object.entries(PRESENCE_META).map(([id, m]) => (
                               <span key={id} className="inline-flex items-center gap-1 text-[9px] font-bold text-slate-600">
                                 <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                                 {m.label}
                               </span>
                             ))}
                           </div>
                        </div>
                    </div>
                    <div className="flex-1 w-full relative z-0">
                        {mapMarkers.length === 0 && (
                            <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/80 pointer-events-none">
                                <p className="text-sm font-medium text-slate-500 px-6 text-center">
                                    {reps.length === 0
                                      ? 'No reps in your scope.'
                                      : `${reps.length} rep(s) — waiting for punch-in or visit GPS. On-duty reps appear here after punch-in with location.`}
                                </p>
                            </div>
                        )}
                        <MapContainer
                            center={mapCenter}
                            zoom={mapMarkers.length > 0 ? 11 : 12}
                            zoomControl={false}
                            style={{ height: '100%', width: '100%' }}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                            />
                            <FitMapBounds positions={mapPositions} boundsKey={mapBoundsKey} />

                            {mapMarkers.map((rep) => {
                                const meta = PRESENCE_META[presenceKey(rep)] || PRESENCE_META.offline;
                                return (
                                <Marker
                                    key={rep.id}
                                    position={[rep.mapLat, rep.mapLng]}
                                    icon={createRepMarkerIcon(rep)}
                                >
                                    <Popup className="premium-popup">
                                        <div className="p-1 min-w-[180px]">
                                            <p className="font-bold text-slate-900">{rep.name || rep.email}</p>
                                            <p className="text-[10px] font-bold uppercase mb-1" style={{ color: meta.color }}>
                                                {meta.label}
                                            </p>
                                            {rep.placeLabel && (
                                                <p className="text-xs font-semibold text-slate-800 mb-1">
                                                    {rep.placeLabel}
                                                </p>
                                            )}
                                            {rep.last_seen && (
                                                <p className="text-[10px] text-slate-500 mt-1">
                                                    Last GPS: {formatIstDateTime(rep.last_seen)}
                                                    {rep.location_source ? ` (${rep.location_source})` : ''}
                                                </p>
                                            )}
                                        </div>
                                    </Popup>
                                </Marker>
                            );
                            })}
                        </MapContainer>
                    </div>
                    {repsAwaitingGps.length > 0 && (
                        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/80 text-xs text-slate-600">
                            <span className="font-semibold">{repsAwaitingGps.length} rep(s) not on map</span>
                            {' — '}
                            {repsAwaitingGps.slice(0, 5).map((r) => r.name || r.email).join(', ')}
                            {repsAwaitingGps.length > 5 ? ` +${repsAwaitingGps.length - 5} more` : ''}
                            {' (no GPS from punch-in or visit yet)'}
                        </div>
                    )}
                </motion.div>

                {/* Right Pane: Status & Recent Logs */}
                <div className="lg:col-span-4 space-y-8 min-w-0">
                    {/* Executive List */}
                    <motion.div 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 }}
                        className="glass-card p-6 flex flex-col h-[280px]"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-slate-800">Team Status</h3>
                            <button className="p-1 hover:bg-slate-100 rounded-lg text-slate-400">
                                <MoreVertical className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="space-y-3 overflow-y-auto pr-2 custom-scrollbar flex-1">
                            {reps.map(rep => (
                                <RepItem key={rep.id} rep={rep} />
                            ))}
                            {reps.length === 0 && <p className="text-sm text-gray-500 text-center py-8 font-medium">No active reps found.</p>}
                        </div>
                    </motion.div>

                    {/* Recent Activities */}
                    <motion.div 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.7 }}
                        className="glass-card p-6 flex flex-col h-[188px]"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-slate-800">Live Feed</h3>
                            <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full uppercase">Recent</span>
                        </div>
                        <div className="space-y-1 overflow-y-auto custom-scrollbar flex-1">
                            {recentActivity.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center py-6">No activity recorded today yet.</p>
                            ) : (
                                recentActivity.map((item, idx) => (
                                    <ActivityItem
                                        key={`${item.type}-${item.time}-${idx}`}
                                        title={item.title}
                                        time={formatRelativeTime(item.time)}
                                        type={item.type === 'alert' ? 'alert' : 'visit'}
                                        status={item.status}
                                    />
                                ))
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default OverviewTab;

